import api from './api';
import tzlookup from 'tz-lookup';
import { getCachedData, setCachedData } from '../utils/apiCache';

const DATE_TIME_CACHE_KEY = 'date-time';
const DATE_TIME_CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours cache duration

export const getTimezoneFromCoordinates = (latitude, longitude) => {
  try {
    // Convert coordinates to numbers if they're strings
    const lat = Number(latitude);
    const lon = Number(longitude);

    // Check if coordinates are valid numbers
    if (isNaN(lat) || isNaN(lon)) {
      console.warn('Invalid coordinates:', { latitude, longitude });
      return null;
    }

    // Check if coordinates are within valid ranges
    if (lat < -90 || lat > 90 || lon < -180 || lon > 180) {
      console.warn('Coordinates out of range:', { lat, lon });
      return null;
    }

    return tzlookup(lat, lon);
  } catch (error) {
    console.error('Error getting timezone:', error);
    return null;
  }
};

export const fetchDateTime = async (timezone) => {
  try {
    // Check cache first
    const cachedData = getCachedData(DATE_TIME_CACHE_KEY);
    const now = new Date();
    const today = now.toDateString();
    
    // Use cached data if available and it's from today
    if (cachedData && cachedData.fetchDate === today) {
      console.log('Using cached date-time data from today');
      return cachedData;
    }

    // If not in cache or from a previous day, make the API request
    console.log('Fetching new date-time data');
    const response = await api.get('/date-time');
    
    // Add today's date to the cached data
    const dataToCache = {
      ...response.data,
      fetchDate: today
    };
    
    // Cache the response with the longer duration
    setCachedData(DATE_TIME_CACHE_KEY, dataToCache);
    
    // Also store in localStorage for persistence across page reloads
    try {
      localStorage.setItem('dateTime', JSON.stringify({
        data: dataToCache,
        timestamp: Date.now()
      }));
    } catch (e) {
      console.warn('Failed to store dateTime in localStorage:', e);
    }
    
    return dataToCache;
  } catch (error) {
    console.error("Error fetching date and time:", error);
    
    // Try to use data from localStorage if API call fails
    try {
      const storedData = localStorage.getItem('dateTime');
      if (storedData) {
        const { data } = JSON.parse(storedData);
        return data;
      }
    } catch (e) {
      console.warn('Failed to retrieve dateTime from localStorage:', e);
    }
    
    throw error;
  }
};

export const getCountdown = (prayerTimes, now = new Date()) => {
  if (!prayerTimes || Object.keys(prayerTimes).length === 0) {
    return { nextPrayer: "Unknown", countdown: "--:--:--" };
  }

  const currentTime = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
  
  const prayers = Object.entries(prayerTimes).map(([name, time]) => {
    const [hours, minutes] = time.split(':').map(Number);
    return { name, time: hours * 3600 + minutes * 60 };
  });

  prayers.sort((a, b) => a.time - b.time);

  let nextPrayer = prayers.find(prayer => prayer.time > currentTime);

  if (!nextPrayer) {
    nextPrayer = prayers[0]; // If no next prayer found, it's Fajr of the next day
  }

  let timeDiff = nextPrayer.time - currentTime;
  if (timeDiff < 0) {
    timeDiff += 24 * 3600; // Add 24 hours if it's Fajr of the next day
  }

  const hours = Math.floor(timeDiff / 3600);
  const minutes = Math.floor((timeDiff % 3600) / 60);
  const seconds = Math.floor(timeDiff % 60);

  const countdown = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  return { nextPrayer: nextPrayer.name, countdown };
};

export const isItPrayerTime = (prayerTimes, now = new Date()) => {
  const currentTime = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
  
  for (const [prayer, time] of Object.entries(prayerTimes)) {
    const [hours, minutes] = time.split(':').map(Number);
    const prayerTime = hours * 3600 + minutes * 60;
    
    if (currentTime >= prayerTime && currentTime < prayerTime + 300) { // 5 minutes window
      return { isPrayerTime: true, currentPrayer: prayer };
    }
  }
  
  return { isPrayerTime: false, currentPrayer: null };
};

export const isExactPrayerTime = (prayerTimes, now = new Date()) => {
  const currentTime = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
  
  for (const [prayer, time] of Object.entries(prayerTimes)) {
    const [hours, minutes] = time.split(':').map(Number);
    const prayerTime = hours * 3600 + minutes * 60;
    
    if (currentTime === prayerTime) {
      return { isPrayerTime: true, currentPrayer: prayer };
    }
  }
  
  return { isPrayerTime: false, currentPrayer: null };
};
